import { BrowserSupport } from '@tableau/browser-support';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~common/react/CommonStyles';

import { classes, cssClass } from '../../../style';
import { LeftNavVisibility } from './LeftNav';

const cssPrefix = 'Navigation';

export namespace NavStyles {
  export const OpenPanelWidth = 240;  // must be kept in sync with updateSiteLogoForm.styl
  export const MinimizedPanelWidth = 66;

  export const LeftPanel = cssClass(cssPrefix, 'left-panel', {
    overflowY: 'auto',
    transition: 'width .3s ease-in-out',
    height: '100%',
    backgroundColor: CommonStyles.Colors.Grey250,  // must be kept in sync with updateSiteLogoForm.styl
    overflowX: 'hidden',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxSizing: 'border-box',
    borderRight: `1px solid ${TabStyles.Colors.L6}`,
    $nest: {
      '@media screen and (max-width: 768px)': {
        display: 'none'
      }
    }
  });

  export const LeftNavExpanded = cssClass(cssPrefix, 'left-panel-shown', {
    width: OpenPanelWidth
  });

  export const MinimizedLeftNav = cssClass(cssPrefix, 'left-panel-minimized', {
    width: MinimizedPanelWidth
  });

  export const MinimizedScrollLeftNav = cssClass(cssPrefix, 'left-panel-minimized-scroll', {
    width: MinimizedPanelWidth + 10,
    ...(BrowserSupport.IsIE() && {width: MinimizedPanelWidth + 18}),
      // site picker takes more space on IE11 so we need to add more space for the scrollbar so it doesn't cut off the site picker
  });

  export function getLeftNavClassNames(leftNavVisibility: LeftNavVisibility, visibleVerticalScroll: boolean) {
    return classes(
      LeftPanel,
      leftNavVisibility === 'expanded' ? LeftNavExpanded :
        visibleVerticalScroll ? MinimizedScrollLeftNav : MinimizedLeftNav
    );
  }

  export const NavList = cssClass(cssPrefix, 'nav-list', {
    zIndex: 0,
    outline: 'none',
    listStyleType: 'none',
    padding: 0,
    margin: 0,
    listStyle: 'none'
  });

  export const LeftPanelButton = cssClass(cssPrefix, 'left-panel', {
    width: '100%',
  });

  export const ButtonStyle: React.CSSProperties = {
    height: 52,
    paddingRight: 0,
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%',
    marginBottom: 8,
    minWidth: 0,
    border: 'none'
  };

  export const ButtonContentIcon = cssClass(cssPrefix, 'nav-icon', {
    height: 24 + 'px',
    width: 24
  });

  const ButtonContentStyle: React.CSSProperties = {
    fontWeight: 'normal',
    fontSize: '12px',
    color: CommonStyles.Colors.NormalTextColor,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    boxSizing: 'border-box',
    backgroundColor: CommonStyles.Colors.Grey250,  // must be kept in sync with updateSiteLogoForm.styl'
  };

  export const ButtonContent = cssClass(cssPrefix, 'left-panel-shown', {
    ...ButtonContentStyle,
    paddingRight: 4,
  });

  export const ButtonContentVerticalScroll = cssClass(cssPrefix, 'left-panel-minimized', {
    ...ButtonContentStyle,
    paddingRight: 14,
  });

  export function getButtonContentClassNames(visibleVerticalScroll: boolean) {
    return classes(
      visibleVerticalScroll ? ButtonContentVerticalScroll : ButtonContent
    );
  }

  export const NavPanel = cssClass(cssPrefix, 'nav-panel', {
    paddingLeft: 0,
    listStyle: 'none'
  });

  export const iconHeight = 18;
  export const NavLinkHeight = 40;

  // absolute positioning here so that we can leave a fixed left hand margin
  // and place the selected bar atop the margin.
  // This ensure the margin doesn't change when animating and no weird shifts
  export const SelectedBarStyle: React.CSSProperties = {
    width: 2,
    height: NavLinkHeight,
    backgroundColor: 'rgb(25,25,25)',
    position: 'absolute',
    top: 0,
    left: 0
  };

  export const focusBoxStyle = (selected: boolean): React.CSSProperties => {
    return {
      width: 'calc(100% - 2px)',
      height: NavLinkHeight-2,
      ...CommonStyles.Focus.Indicator,
      borderLeftWidth: selected ? 0 : 1,
      backgroundColor: 'transparent',
      pointerEvents: 'none',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 1,
    };
  };

  export const NavLinkContainer = cssClass(cssPrefix, 'nav-link-container', {
    height: '100%',
    boxSizing: 'border-box',
  });

  export const NavLinkValue = cssClass(cssPrefix, 'nav-link-value', {
    position: 'relative',
    fontSize: '14px',
    verticalAlign: 'middle',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: NavLinkHeight,
    outline: 'none',
    textDecoration: 'none',
    $nest: {
      '&:hover': {
        textDecoration: 'none',
      },
      '&:focus': {
        textDecoration: 'none',
      }
    }
  });

  export const CurrentNavLink = cssClass(cssPrefix, 'current-link', {
    backgroundColor: 'rgb(241,241,241)'
  });

  export const HoverNavLink = cssClass(cssPrefix, 'current-link', {
    backgroundColor: CommonStyles.Colors.Grey235
  });

  export function getNavLinkClassNames(selected: boolean, hover: boolean, focus: boolean) {
    return classes(
      NavLinkValue,
      hover ? HoverNavLink : (selected || focus) ? CurrentNavLink : undefined
    );
  }

  export const NavLinkText = cssClass(cssPrefix, 'nav-link-text', {
    fontWeight: 'normal',
    color: CommonStyles.Colors.NormalTextColor,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    lineHeight: '16px',
    paddingTop: 2, // center-ish text with icon
  });

  export const CurrentNavLinkText = cssClass(cssPrefix, 'current-link-text', {
    color: 'rgb(25,25,25)'
  });

  export function getNavLinkTextClassNames(isSelected: boolean) {
    return classes(
      NavLinkText,
      isSelected ? CurrentNavLinkText : undefined
    );
  }

  export const NavIcon = cssClass(cssPrefix, 'nav-icon-only', {
    height: iconHeight + 'px',
    marginRight: 24,
    marginLeft: 24
  });

  export const NavIconByText = cssClass(cssPrefix, 'nav-icon-by-text-only', {
    height: iconHeight + 'px',
    marginRight: 12,
    marginLeft: 24
  });

  const separator: React.CSSProperties = {
    height: 1,
    backgroundColor: TabStyles.Colors.L9Disabled,
    marginBottom: 16,
    marginTop: 16,
  };

  export const SeparatorCollapsed = cssClass(cssPrefix, 'separator-collapsed', {
    ...separator,
    marginRight: 8,
    marginLeft: 8,
  });

  export const SeparatorExpanded = cssClass(cssPrefix, 'separator-expanded', {
    ...separator,
    marginRight: 12,
    marginLeft: 12,
  });

  export const SeparatorSitePicker = cssClass(cssPrefix, 'separator-site-picker', {
    ...separator,
    marginBottom: TabStyles!.Spacing!.DefaultSpace,
    marginTop: TabStyles!.Spacing!.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    marginLeft: TabStyles.Spacing.DefaultSpace,
  });

  export const NavPanelFlyoutContainer = cssClass(cssPrefix, 'nav-panel-flyout-container', {
    width: '100%',
    height: '100%',
    transition: 'transform .3s ease-in-out',
    transform: 'translateX(-100%)',
    willChange: 'transform',
  });

  // This must match the time in transition below
  export const NavPanelFlyoutHideAnimationTime = 150;
  export const NavPanelFlyoutContainerVisible = cssClass(cssPrefix, 'nav-panel-flyout-container-visible', {
    transform: 'translateX(0)',
    transition: 'transform .15s linear',
  });

  export function getNavPanelFlyoutContainer(startingShow: boolean) {
    return classes(
      NavPanelFlyoutContainer,
      startingShow ? undefined : NavPanelFlyoutContainerVisible
    );
  }

  export const LogoMarginTop = 0;
  export const LogoMarginBottom = 24;  // keep in sync with updateSiteLogoForm.styl

  export const LogoImage: React.CSSProperties = {
    display: 'flex',
    marginRight: 'auto',
    marginLeft: 'auto',
  };

  export const LogoFocus = cssClass(cssPrefix, 'logo-focus', {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: '1px',
    borderColor: 'transparent',
    $nest: {
      // Using polyfill class for focus-visible
      [`&.focus-visible`]: CommonStyles.Focus.Indicator
    }
  });
}
